import {
  ConfigChange,
  ResourceField,
  ServiceValues,
  WorkloadValues,
  changePath,
  resourceValue,
  workloadValuesFor,
} from './valuesModel';

/**
 * What the configuration form may change, and within what bounds.
 *
 * There is no allowlist of keys yet — every configMap key is editable unless
 * it looks like it carries a secret. Secrets have their own plane
 * (SealedSecrets today, Key Vault under ADR-SECRETS-001) and a value that
 * belongs there must not be typed into a ConfigMap from here, whatever the
 * key is called. The check is by name because that is all a form can see.
 *
 * Shared by the browser, which uses it to grey out what cannot be changed,
 * and by the scaffolder action, which is where it is enforced.
 */
const SECRET_LIKE =
  /secret|password|passwd|token|apikey|api_key|sdkkey|sdk_key|signingkey|signing_key|connectionstring|connection_string|credential|private[-_]?key/i;

export function isSecretLikeKey(key: string): boolean {
  return SECRET_LIKE.test(key);
}

/** Environment variable names as .NET and the chart accept them: letters,
 * digits, `_`, and the `.` that logger categories carry
 * (`Logging__LogLevel__Microsoft.AspNetCore`). */
const CONFIG_KEY = /^[A-Za-z_][A-Za-z0-9_.]*$/;

export function isValidConfigKey(key: string): boolean {
  return CONFIG_KEY.test(key);
}

/** Whether a configMap key may be set from the portal for this service. A key
 * fed from the Secret in any environment stays out everywhere: the same name
 * in a ConfigMap would shadow it, or worse, hold the value in plain text. */
export function isEditableKey(key: string, values: ServiceValues): boolean {
  if (!isValidConfigKey(key) || isSecretLikeKey(key)) {
    return false;
  }
  const lowered = key.toLowerCase();
  return !values.workloads.some(workload =>
    workload.secrets.some(secret => secret.toLowerCase() === lowered),
  );
}

export const LIMITS = {
  replicas: { min: 1, max: 10 },
  cpuMillicores: { min: 10, max: 2000 },
  memoryMi: { min: 64, max: 4096 },
} as const;

/** `10m` → 10, `0.5` → 500, `1` → 1000. Null when it is not a CPU quantity. */
export function parseCpuMillicores(quantity: string): number | null {
  const trimmed = quantity.trim();
  const milli = /^(\d+)m$/.exec(trimmed);
  if (milli) {
    return Number(milli[1]);
  }
  const cores = /^(\d+(?:\.\d+)?)$/.exec(trimmed);
  if (cores) {
    return Math.round(Number(cores[1]) * 1000);
  }
  return null;
}

/** `256Mi` → 256, `1Gi` → 1024, `512M` → 488 (decimal units are read, never
 * written). Null when it is not a memory quantity. */
export function parseMemoryMi(quantity: string): number | null {
  const match = /^(\d+(?:\.\d+)?)(Ki|Mi|Gi|Ti|K|M|G|T)?$/.exec(quantity.trim());
  if (!match) {
    return null;
  }
  const amount = Number(match[1]);
  const MI = 1024 * 1024;
  const bytes: Record<string, number> = {
    '': 1,
    Ki: 1024,
    Mi: MI,
    Gi: MI * 1024,
    Ti: MI * 1024 * 1024,
    K: 1000,
    M: 1000 * 1000,
    G: 1000 * 1000 * 1000,
    T: 1000 * 1000 * 1000 * 1000,
  };
  return Math.round((amount * bytes[match[2] ?? '']) / MI);
}

export interface ValidationIssue {
  /** Index into the changes that were validated; absent for issues about the
   * set as a whole. */
  index?: number;
  message: string;
}

function describe(change: ConfigChange): string {
  const where = `${change.workload} in ${change.environment}`;
  switch (change.kind) {
    case 'configMap':
      return `${change.key} (${where})`;
    case 'replicaCount':
      return `replicas (${where})`;
    case 'resource':
      return `${change.field} (${where})`;
    default:
      return where;
  }
}

function checkMemory(
  field: ResourceField,
  value: string,
): { issue?: string; mi?: number } {
  const mi = parseMemoryMi(value);
  if (mi === null) {
    return { issue: `${value} is not a memory quantity — write it like 256Mi or 1Gi.` };
  }
  if (!/(Mi|Gi)$/.test(value.trim())) {
    return { issue: `write memory in Mi or Gi, as the overlays do (${field}).` };
  }
  if (mi < LIMITS.memoryMi.min || mi > LIMITS.memoryMi.max) {
    return {
      issue: `${value} is outside ${LIMITS.memoryMi.min}Mi–${LIMITS.memoryMi.max}Mi.`,
    };
  }
  return { mi };
}

/**
 * Every reason a set of changes cannot be applied, or an empty list.
 *
 * Checked against `values` as they are now, so the caller decides what "now"
 * is: the browser passes what it is showing, the action passes what it just
 * read from the file — and only the second one counts.
 */
export function validateChanges(
  changes: ConfigChange[],
  values: ServiceValues,
): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  const seen = new Map<string, number>();
  // The memory of each env × workload after every change in the set, so a
  // request raised in one change and a limit raised in another are judged
  // together.
  const memory = new Map<string, { requests?: number; limits?: number }>();

  const memoryOf = (workload: WorkloadValues) => {
    const id = `${workload.environment}/${workload.workload}`;
    if (!memory.has(id)) {
      const requests = workload.resources.requests.memory;
      const limits = workload.resources.limits.memory;
      memory.set(id, {
        requests: requests ? parseMemoryMi(requests) ?? undefined : undefined,
        limits: limits ? parseMemoryMi(limits) ?? undefined : undefined,
      });
    }
    return memory.get(id)!;
  };

  changes.forEach((change, index) => {
    const path = [change.environment, ...changePath(change)].join('/');
    const earlier = seen.get(path);
    if (earlier !== undefined) {
      issues.push({
        index,
        message: `${describe(change)} is changed twice (also change ${earlier + 1}).`,
      });
      return;
    }
    seen.set(path, index);

    const workload = workloadValuesFor(values, change.environment, change.workload);
    if (!workload) {
      issues.push({
        index,
        message: `${change.workload} has no overlay in ${change.environment}.`,
      });
      return;
    }
    if (!workload.enabled) {
      issues.push({
        index,
        message: `${change.workload} is disabled in ${change.environment}; nothing deploys from it.`,
      });
      return;
    }

    switch (change.kind) {
      case 'configMap': {
        if (!isValidConfigKey(change.key)) {
          issues.push({ index, message: `${change.key} is not a valid variable name.` });
          break;
        }
        if (!isEditableKey(change.key, values)) {
          issues.push({
            index,
            message: `${change.key} looks like it carries a secret; set it through the secrets plane, not a ConfigMap.`,
          });
          break;
        }
        if (change.value.includes('\n') || change.value.includes('\r')) {
          issues.push({ index, message: `${describe(change)} cannot span lines.` });
          break;
        }
        const current = workload.configMap[change.key];
        if (current?.style === 'block') {
          issues.push({
            index,
            message: `${describe(change)} is a multi-line value; edit it in git.`,
          });
          break;
        }
        if (current && current.value === change.value) {
          issues.push({ index, message: `${describe(change)} already holds that value.` });
        }
        break;
      }
      case 'replicaCount': {
        if (!Number.isInteger(change.value)) {
          issues.push({ index, message: `${describe(change)} must be a whole number.` });
          break;
        }
        if (change.value < LIMITS.replicas.min || change.value > LIMITS.replicas.max) {
          issues.push({
            index,
            message: `${describe(change)} must be between ${LIMITS.replicas.min} and ${LIMITS.replicas.max}.`,
          });
          break;
        }
        if (workload.autoscaling?.enabled) {
          issues.push({
            index,
            message: `${change.workload} in ${change.environment} is autoscaled (${workload.autoscaling.minReplicas ?? '?'}–${workload.autoscaling.maxReplicas ?? '?'}); replicaCount is ignored while the HPA is on.`,
          });
          break;
        }
        if (workload.replicaCount === change.value) {
          issues.push({ index, message: `${describe(change)} is already ${change.value}.` });
        }
        break;
      }
      case 'resource': {
        const current = resourceValue(workload.resources, change.field);
        if (change.field === 'requests.cpu') {
          const millicores = parseCpuMillicores(change.value);
          if (millicores === null) {
            issues.push({
              index,
              message: `${change.value} is not a CPU quantity — write it like 10m or 0.5.`,
            });
            break;
          }
          if (
            millicores < LIMITS.cpuMillicores.min ||
            millicores > LIMITS.cpuMillicores.max
          ) {
            issues.push({
              index,
              message: `${change.value} is outside ${LIMITS.cpuMillicores.min}m–${LIMITS.cpuMillicores.max}m.`,
            });
            break;
          }
        } else {
          const checked = checkMemory(change.field, change.value);
          if (checked.issue) {
            issues.push({ index, message: checked.issue });
            break;
          }
          const state = memoryOf(workload);
          if (change.field === 'requests.memory') state.requests = checked.mi;
          else state.limits = checked.mi;
        }
        if (current === change.value) {
          issues.push({ index, message: `${describe(change)} is already ${change.value}.` });
        }
        break;
      }
      default:
        issues.push({ index, message: 'Unknown kind of change.' });
    }
  });

  // Judged once per env × workload after all its changes, so the message
  // names the pair rather than whichever change happened to come last.
  for (const [id, state] of memory) {
    if (
      state.requests !== undefined &&
      state.limits !== undefined &&
      state.limits < state.requests
    ) {
      issues.push({
        message: `${id.replace('/', ' ')}: memory limit (${state.limits}Mi) would be below the request (${state.requests}Mi).`,
      });
    }
  }

  return issues;
}
